import React, { useEffect, useState } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BookOpen, FolderOpen, FileText, Star, ExternalLink } from "lucide-react";
import { formatDriveUrl } from "../../../../Utils/driveUtils";

import {
  Button,
  List,
  Progress,
  message,
  Spin,
  Modal,
  Divider,
  Tabs,
  Tag,
  Select,
  Alert,
  Rate,
  Checkbox,
  Input,
} from "antd";
import {
  EditOutlined,
  LoadingOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  WhatsAppOutlined,
  HeartOutlined,
  CreditCardOutlined,
  StarOutlined,
} from "@ant-design/icons";

import Plyr from "plyr-react";
// import "plyr-react/dist/plyr.css";
import "plyr/dist/plyr.css";

import 'react-quill/dist/quill.bubble.css';

// API
import {
  addCourseToWishList,
  buyACourse,
  enrollToCourseContent,
  getAllUsersOfACourse,
  getCourseById,
  getCourseContentByRoleAndCourseId,
  getUsersCourses,
  getAllPayments,
  getNotesByUser,
  editCourse,
  getAllUsers,
  submitCourseFeedback,
  getUserCourseFeedback,
} from "../../../../Constants/Api/functions/function";

import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import nodata from "../../../../Assets/Images/nodata.png";
import video from "../../../../Assets/Images/video.png";
import files from "../../../../Assets/Images/file.png";
import free from "../../../../Assets/Images/free.png";

import "./style.scss";
import CourseUsers from "./CourseUsers";
import { setUsersCources } from "../../../../Reducers/usersCourcesReducer";
import buyImage from "../../../../Assets/Images/buynow.png";
import CourseAssessments from "./CourseAssessments";

const NextInstallmentBanner = ({ installmentData }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [isOverdue, setIsOverdue] = useState(false);
  const [formattedDueDate, setFormattedDueDate] = useState('');

  const { nextInstallmentNumber, totalInstallments, amount, dueDateStr, courseId } = installmentData || {};

  useEffect(() => {
    if (!dueDateStr) {
      setTimeLeft('');
      return;
    }

    const dueDate = new Date(dueDateStr);
    try {
      setFormattedDueDate(
        dueDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      );
    } catch {
      setFormattedDueDate(dueDateStr.split('T')[0]);
    }

    const calculateTime = () => {
      const now = new Date();
      const difference = +dueDate - +now;

      if (difference < 0) {
        setIsOverdue(true);
        const absDiff = Math.abs(difference);
        const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((absDiff / (1000 * 60 * 60)) % 24);
        const mins = Math.floor((absDiff / (1000 * 60)) % 60);
        const secs = Math.floor((absDiff / 1000) % 60);

        if (days > 0) {
          return `Overdue by ${days}d ${hours}h ${mins}m`;
        }
        return `Overdue by ${hours}h ${mins}m ${secs}s`;
      }

      setIsOverdue(false);
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const mins = Math.floor((difference / (1000 * 60)) % 60);
      const secs = Math.floor((difference / 1000) % 60);

      if (days > 0) {
        return `${days}d ${hours}h ${mins}m ${secs}s`;
      }
      return `${hours}h ${mins}m ${secs}s`;
    };

    setTimeLeft(calculateTime());
    const timer = setInterval(() => {
      setTimeLeft(calculateTime());
    }, 1000);

    return () => clearInterval(timer);
  }, [dueDateStr]);

  if (!installmentData) return null;

  const paymentUrl = `/dashboard/cources/${courseId}/buy?installment=${nextInstallmentNumber}`;

  return (
    <div className={`next-installment-banner ${isOverdue ? 'overdue' : 'upcoming'}`}>
      <div className="banner-content">
        <div
          className="banner-icon"
          style={{
            background: isOverdue ? '#ff4d4f' : '#1890ff',
            color: '#fff',
            boxShadow: isOverdue ? '0 2px 8px rgba(255, 77, 79, 0.3)' : '0 2px 8px rgba(24, 144, 255, 0.3)',
          }}
        >
          {isOverdue ? '⚠️' : '⏱'}
        </div>
        <div className="banner-info">
          <div className="banner-title-row">
            <span className="banner-title" style={{ color: isOverdue ? '#cf1322' : '#003a8c' }}>
              Next Payment: Installment {nextInstallmentNumber} {totalInstallments ? `of ${totalInstallments}` : ''}
            </span>
            {amount && (
              <Tag color={isOverdue ? 'red' : 'blue'} style={{ fontWeight: '600', borderRadius: '4px' }}>
                {Number(amount).toLocaleString()} LKR
              </Tag>
            )}
          </div>
          <div className="banner-countdown-row" style={{ color: isOverdue ? '#cf1322' : '#096dd9' }}>
            {dueDateStr ? (
              <>
                <span>
                  <strong>{isOverdue ? 'Overdue: ' : 'Due in: '}</strong>
                  <span className="countdown-clock">{timeLeft}</span>
                </span>
                {formattedDueDate && (
                  <span className="due-date-label">(Due Date: {formattedDueDate})</span>
                )}
              </>
            ) : (
              <span>Your next installment is ready for payment.</span>
            )}
          </div>
        </div>
      </div>

      <div className="banner-action">
        <Link to={paymentUrl}>
          <Button
            type="primary"
            size="middle"
            danger={isOverdue}
            icon={<CreditCardOutlined />}
            className="pay-installment-btn"
            style={{
              fontWeight: '600',
              height: '40px',
              padding: '0 20px',
              borderRadius: '8px',
              background: isOverdue ? '#ff4d4f' : '#1890ff',
              boxShadow: isOverdue ? '0 2px 6px rgba(255, 77, 79, 0.35)' : '0 2px 6px rgba(24, 144, 255, 0.35)',
            }}
          >
            Pay Next Installment {amount ? `(${Number(amount).toLocaleString()} LKR)` : ''}
          </Button>
        </Link>
      </div>
    </div>
  );
};

const Course = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEnrollLoading, setIsEnrollLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const courseId = useParams().id;
  const [selectedContentId, setSelectedContentId] = useState();
  const [courseContents, setCourseContents] = useState();
  const [completedPercentage, setCompletedPercentage] = useState(0);
  const [courseNotes, setCourseNotes] = useState([]);
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  const [data, setData] = useState();
  const [course, setCourse] = useState();
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const allCources = useSelector((state) => state.allCources.allCources);
  const usersCources = useSelector((state) => state.usersCources.usersCources);
  const [userCources, setUserCources] = useState();
  const [isCourseUnlocked, setIsCourseUnlocked] = useState(false);
  const [isContentUnlocked, setIsContentUnlocked] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [courseUsers, setCourseusers] = useState(null);
  const [openCourseUsers, setOpenCourseUsers] = useState(false);
  const [showEntrollPopupForFreeCourse, setShowEntrollPopupForFreeCourse] =
    useState(false);
  const [showEntrollPopupForPaidCources, setShowEntrollPopupForPaidCources] =
    useState(false);
  const [showLockedContentPrompt, setShowLockedContentPrompt] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCoordinatorId, setSelectedCoordinatorId] = useState(undefined);
  const [coordinators, setCoordinators] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);

  const isAssignedInstructor = userProfile?.role === "instructor" && userProfile?.id && (String(course?.instructorId) === String(userProfile.id) || (course?.instructorIds && course.instructorIds.includes(userProfile.id)));
  const isCoordinator = userProfile?.role === "coordinator";
  const isSuperAdmin = userProfile?.role === "superadmin" || userProfile?.role === "admin" || userProfile?.role === "instructor" || isCoordinator;

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [ratingCourse, setRatingCourse] = useState(5);
  const [ratingLecturer, setRatingLecturer] = useState(5);
  const [contentClarity, setContentClarity] = useState(5);
  const [feedbackComments, setFeedbackComments] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  const handleOpenFeedbackModal = async () => {
    setFeedbackModalOpen(true);
    if (userProfile?.id && courseId) {
      try {
        const res = await getUserCourseFeedback(userProfile.id, courseId);
        if (res && res.hasSubmitted && res.feedback) {
          setRatingCourse(res.feedback.ratingCourse || 5);
          setRatingLecturer(res.feedback.ratingLecturer || 5);
          setFeedbackComments(res.feedback.comments || '');
        }
      } catch (e) {
        console.error('Error fetching existing feedback:', e);
      }
    }
  };

  const handleSubmitFeedback = async () => {
    if (!userProfile?.id || !courseId) return;
    setFeedbackSubmitting(true);
    try {
      const res = await submitCourseFeedback({
        userId: userProfile.id,
        courseId,
        lecturerId: course?.instructorId || course?.createdBy,
        ratingCourse,
        ratingLecturer,
        contentClarity,
        comments: feedbackComments,
        isAnonymous,
      });
      if (res && res.success) {
        openMessage("success", res.message || "Feedback submitted successfully!");
        setFeedbackModalOpen(false);
      } else {
        openMessage("error", res?.message || "Failed to submit feedback");
      }
    } catch (e) {
      console.error(e);
      openMessage("error", "Error submitting feedback");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === "coordinator" && course && course.id) {
      if (!course.coordinatorId || String(course.coordinatorId) !== String(userProfile.id)) {
        openMessage("error", "You are only authorized to view details of courses assigned to you.");
        history.push("/dashboard/cources");
      }
    }
  }, [userProfile, course, history]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (userProfile?.role === "superadmin" || userProfile?.role === "admin") {
        try {
          const users = await getAllUsers(2); // Center 2
          if (users) {
            const filteredCoordinators = users.filter((u) => u.role === "coordinator");
            setCoordinators(filteredCoordinators);
            const filteredInstructors = users.filter((u) => u.role === "instructor");
            setInstructors(filteredInstructors);
          }
        } catch (error) {
          console.error("Failed fetching users:", error);
        }
      }
    };
    fetchUsers();
  }, [userProfile]);

  const handleAssignCoordinator = async () => {
    setIsAssigning(true);
    try {
      const selectedCoord = coordinators.find((c) => c.id === selectedCoordinatorId);
      const response = await editCourse({
        id: courseId,
        coordinatorId: selectedCoordinatorId || null,
        coordinatorName: selectedCoord ? selectedCoord.name : null,
        coordinatorWhatsApp: selectedCoord ? selectedCoord.contactNumber : null,
      });
      if (response && response.status === 200) {
        openMessage("success", "Coordinator assigned successfully");
        setShowAssignModal(false);
        await handleGetCourse();
      } else {
        openMessage("error", "Failed to assign coordinator");
      }
    } catch (error) {
      console.error("handleAssignCoordinator error:", error);
      openMessage("error", "Failed to assign coordinator");
    } finally {
      setIsAssigning(false);
    }
  };

  useEffect(() => {
    if (allCources) {
      setCourse(allCources.find((item) => item.id === courseId));
    } else {
      handleGetCourse();
    }
  }, [allCources]);

  useEffect(() => {
    if (userProfile?.role !== "superadmin" && userProfile?.role !== "admin") {
      if (usersCources) {
        setUserCources(usersCources);
      } else {
        handleGetUserCources();
      }
    }
  }, [usersCources]);

  useEffect(() => {
    if (isSuperAdmin) {
      setIsCourseUnlocked(true);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const isStaff = isSuperAdmin;
    if (isStaff) {
      setShowEntrollPopupForFreeCourse(false);
      setShowEntrollPopupForPaidCources(false);
      return;
    }

    if (userCources && course) {
      const isCourseInUserCourses = userCources?.find(
        (item) => item.id === course.id
      );
      const lastVisit = localStorage.getItem("digital-academy-last-visit");
      if (lastVisit !== courseId) {
        if (!isCourseInUserCourses && course?.price === 0) {
          setShowEntrollPopupForFreeCourse(true);
        } else {
          setShowEntrollPopupForFreeCourse(false);
        }

        if (!isCourseInUserCourses && course?.price !== 0) {
          setShowEntrollPopupForPaidCources(true);
        } else {
          setShowEntrollPopupForPaidCources(false);
        }

        localStorage.removeItem("digital-academy-last-visit");
      } else {
        setShowEntrollPopupForFreeCourse(false);
        setShowEntrollPopupForPaidCources(false);
        localStorage.removeItem("digital-academy-last-visit");
      }
    }
  }, [userCources, course, userProfile]);

  // Derive whether the course is in user's courses
  const isCourseInUserCourses = userCources?.find(
    (item) => item.id === course?.id || item.courseId === course?.id || item.id === courseId || item.courseId === courseId
  );

  const getNextInstallmentData = (userCourse, currentCourse) => {
    if (!userCourse || userCourse.isPaid || !userCourse.payedInstallments || Number(userCourse.payedInstallments) <= 0) {
      return null;
    }
    const paidCount = Number(userCourse.payedInstallments) || 0;
    const nextInstNum = paidCount + 1;

    let breakdown = [];
    try {
      const raw = userCourse.installmentBreakdown || currentCourse?.installmentBreakdown;
      breakdown = typeof raw === 'string' ? JSON.parse(raw) : (raw || []);
      if (!Array.isArray(breakdown) || breakdown.length === 0) {
        const alumniRaw = userCourse.alumniInstallmentBreakdown || currentCourse?.alumniInstallmentBreakdown;
        breakdown = typeof alumniRaw === 'string' ? JSON.parse(alumniRaw) : (alumniRaw || []);
      }
    } catch (e) {
      console.error("Error parsing breakdown", e);
    }

    const totalInstallments = (breakdown && breakdown.length > 0)
      ? breakdown.length
      : (currentCourse?.installments || 0);

    if (totalInstallments > 0 && paidCount >= totalInstallments) {
      return null;
    }

    const nextInstItem = (breakdown && breakdown.length > 0)
      ? (breakdown.find((i) => Number(i.installmentNumber) === nextInstNum) || breakdown[paidCount])
      : null;

    return {
      nextInstallmentNumber: nextInstNum,
      totalInstallments: totalInstallments,
      amount: nextInstItem?.amount || null,
      dueDateStr: nextInstItem?.dueDate || null,
      courseId: currentCourse?.id || userCourse.courseId || userCourse.id || courseId,
    };
  };
  const nextInstallmentData = getNextInstallmentData(isCourseInUserCourses, course);

  useEffect(() => {
    if (!courseContents) return;
    try {
      const userCourse = userCources?.find((item) => item.id === courseId);

      // Full course access: only when fully paid (or superadmin or assigned instructor)
      const hasFullAccess = isSuperAdmin || !!userCourse?.isPaid;

      if (hasFullAccess !== isCourseUnlocked) {
        setIsCourseUnlocked(hasFullAccess);
      }

      const content = courseContents.find(
        (item) => item.id === selectedContentId
      );

      // A single content is unlocked when:
      // - superadmin/staff, OR
      // - content is individually free, OR
      // - backend says hasAccess=1 (handles installments & enrollment)
      const unlocked =
        !!content &&
        (isSuperAdmin ||
          content?.isFree === 1 ||
          content?.isFree === true ||
          content?.hasAccess === 1 ||
          content?.hasAccess === true);

      setIsContentUnlocked(unlocked);
    } catch (error) {
      console.error("isContentUnlocked", error);
    }
  }, [
    courseContents,
    selectedContentId,
    userCources,
    course,
    courseId,
    userProfile,
    isCourseUnlocked,
  ]);

  useEffect(() => {
    getCourseContents();
    getCourseNotes();

    if (userProfile?.role === "superadmin" || userProfile?.role === "admin" || userProfile?.role === "coordinator" || userProfile?.role === "instructor") {
      getCourseUsers();
    }
  }, [userProfile]);

  const getCourseNotes = async () => {
    if (!userProfile) return;
    setIsNotesLoading(true);
    try {
      const response = await getNotesByUser(userProfile.id, userProfile.role);
      if (response && Array.isArray(response)) {
        const filtered = response.filter(n => n.courseId === courseId);
        setCourseNotes(filtered);
      }
    } catch (err) {
      console.error("getCourseNotes error:", err);
    } finally {
      setIsNotesLoading(false);
    }
  };

  const handleGetUserCources = async () => {
    setIsLoading(true);
    try {
      const userCources = await getUsersCourses(userProfile?.id);

      setUserCources(userCources);
      dispatch(setUsersCources(userCources));
    } catch (error) {
      console.error("handleGetUserCources", error);
    }
    setIsLoading(false);
  };

  const handleGetCourse = async () => {
    setIsLoading(true);
    try {
      const response = await getCourseById(courseId);
      if (response) {
        setCourse(response);
      }
    } catch (error) {
      console.error("handleGetCourse", error);
    }
    setIsLoading(false);
  };

  const getCourseUsers = async () => {
    setIsLoadingUsers(true);
    const response = await getAllUsersOfACourse(courseId);
    setCourseusers(response);
    setIsLoadingUsers(false);
  };

  const getCourseContents = async () => {
    setIsLoading(true);
    try {
      let response = await getCourseContentByRoleAndCourseId(
        courseId,
        userProfile.role,
        userProfile.id
      );
      if (response) {
        setCourseContents(response);

        response.sort((a, b) => {
          return new Date(a.createdAt) - new Date(b.createdAt);
        });

        const completedContents = response.filter(
          (item) => item?.participation?.id
        );
        const percentage = (completedContents.length / response.length) * 100;
        setCompletedPercentage(
          percentage % 1 === 0 ? percentage : percentage.toFixed(1)
        );

        setData(
          response.map((item, index) => {
            return {
              id: item.id,
              title: item.title,
              status: item?.participation?.id ? "completed" : "pending",
              duration: item.duration,
            };
          })
        );

        // Find first accessible content, or first content if none accessible
        const accessibleContent = response.find(
          (item) =>
            item?.hasAccess === 1 ||
            item?.hasAccess === true ||
            item?.isFree === 1
        );
        if (accessibleContent) {
          setSelectedContentId(accessibleContent.id);
        } else if (response[0]) {
          // If no accessible content, still select first one (will be locked/faded)
          setSelectedContentId(response[0].id);
        }
      }
    } catch (error) {
      console.error("getCourseContents", error);
    }
    setIsLoading(false);
  };

  const handleMarkAsLearned = async () => {
    setIsEnrollLoading(true);
    try {
      const response = await enrollToCourseContent(
        courseId,
        userProfile.id,
        selectedContentId
      );

      if (response && response.status === 200) {
        openMessage("success", "Marked as learned");
        getCourseContents();
      } else {
        openMessage("error", "Error occured while marking as learned");
      }
    } catch (error) {
      console.error("handleMarkAsLearned", error);
      openMessage("error", "Error occured while marking as learned");
    }
    setIsEnrollLoading(false);
  };

  const openMessage = (type, content) => {
    messageApi.open({
      type: type,
      content: content,
    });
  };

  const hnadleEnrollToFreeCourse = async () => {
    setIsLoading(true);
    try {
      const values = {
        courseId: courseId,
        userId: userProfile.id,
        isPaid: false,
        isAddToPayments: true,
      };

      const response = await buyACourse(values);

      if (response && response.status === 200) {
        openMessage("success", "You have successfully enrolled to this course");
        getCourseContents();
      } else {
        openMessage("error", "Error occured while enrolling to this course");
      }
    } catch (error) {
      console.error("hnadleEnrollToFreeCourse", error);
      openMessage("error", "Error occured while enrolling to this course");
    }
    setIsLoading(false);
    setShowEntrollPopupForFreeCourse(false);
  };

  const isCourseInWishList = () => {
    const userWishList = userProfile?.courseWishlist;
    const wishlistIds = userWishList?.split(",");
    wishlistIds?.pop();

    return wishlistIds?.includes(courseId);
  };

  const handleAddToWishList = async () => {
    setIsLoading(true);
    try {
      const response = await addCourseToWishList(userProfile.id, courseId);
      if (response) {
        openMessage("success", "Course added to wishlist");
      } else {
        openMessage("error", "Error occured while adding to wishlist");
      }
    } catch (error) {
      console.error("handleAddToWishList", error);
      openMessage("error", "Error occured while adding to wishlist");
    }

    setShowEntrollPopupForPaidCources(false);
    setIsLoading(false);
  };

  const handleContentSelect = (contentId, isLocked) => {
    // Prevent selecting locked content
    if (isLocked) {
      setShowLockedContentPrompt(true);
      return; // Don't change selected content if it's locked
    }
    setSelectedContentId(contentId);
    setShowLockedContentPrompt(false);
  };

  const isContentLockedForUser = (contentId) => {
    const content = courseContents?.find((item) => item.id === contentId);
    if (!content) return true; // Lock if content not found
    if (isSuperAdmin) return false; // Superadmin always has access

    // Free content is always accessible
    if (content?.isFree === 1 || content?.isFree === true) return false;

    // Backend access flag (handles enrollment + installments + free courses after enroll)
    if (content?.hasAccess === 1 || content?.hasAccess === true) return false;

    // Otherwise, lock it for students (item stays visible but faded/unclickable)
    return userProfile?.role === "student";
  };

  return (
    <div className="course">
      {contextHolder}
      <Modal
        className="freeCoursePopup"
        title=""
        footer={null}
        centered
        open={showEntrollPopupForFreeCourse}
        onOk={() => setShowEntrollPopupForFreeCourse(false)}
        onCancel={() => setShowEntrollPopupForFreeCourse(false)}
      >
        <div className="confirmEnrollPopup">
          <div className="image">
            <img src={free} alt="Buy Course" />
          </div>
          <h1>Enroll to this course for free</h1>
          <p>
            This course is free for you. You can enroll to this course for free.
            Do you want to enroll to this course?
          </p>
          <div className="actions">
            <Button
              type="primary"
              onClick={hnadleEnrollToFreeCourse}
              icon={isLoading ? <LoadingOutlined /> : null}
            >
              Enroll
            </Button>
            <Button
              type="default"
              onClick={() => setShowEntrollPopupForFreeCourse(false)}
            >
              Cancel
            </Button>
            {course?.whatsappGroupLink && (
              <a
                href={course?.whatsappGroupLink}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  type="primary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#25d366",
                  }}
                >
                  <WhatsAppOutlined />
                  Join Whatsapp Group
                </Button>
              </a>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        title="Assign Coordinator"
        open={showAssignModal}
        onOk={handleAssignCoordinator}
        confirmLoading={isAssigning}
        onCancel={() => setShowAssignModal(false)}
        okText="Assign"
        cancelText="Cancel"
        destroyOnClose
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: "8px" }}>Select a coordinator to assign to this course:</p>
          <Select
            placeholder="Select Coordinator"
            style={{ width: "100%" }}
            value={selectedCoordinatorId}
            onChange={(val) => setSelectedCoordinatorId(val)}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {coordinators.map((coord) => (
              <Select.Option key={coord.id} value={coord.id}>
                {coord.name} ({coord.email})
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>

      <Modal
        className="paidCoursePopup"
        title=""
        footer={null}
        centered
        open={showEntrollPopupForPaidCources}
        onOk={() => setShowEntrollPopupForPaidCources(false)}
        onCancel={() => setShowEntrollPopupForPaidCources(false)}
      >
        <div className="confirmEnrollPopup">
          <div className="image">
            {course?.videoUrl && (
              <Plyr
                source={{
                  type: "video",
                  sources: [
                    {
                      src: course?.videoUrl,
                      provider: "youtube",
                    },
                  ],
                }}
              />
            )}
          </div>
          <h1>Enroll to this course</h1>
          {!isCourseInWishList() ? (
            <p>
              You can enroll to this course by buying it. Or you can add this
              course to your wishlist and buy it later.
            </p>
          ) : (
            <p>You can enroll to this course by buying it.</p>
          )}
          <div className="actions">
            <Link to={`/dashboard/cources/${courseId}/buy`}>
              <Button type="primary" icon={<ShoppingCartOutlined />}>
                Buy This Course
              </Button>
            </Link>

            {!isCourseInWishList() && (
              <Button
                type="default"
                icon={<HeartOutlined />}
                onClick={handleAddToWishList}
              >
                Add to Wishlist
              </Button>
            )}
            {course?.whatsappGroupLink && (
              <a
                href={course?.whatsappGroupLink}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  type="primary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#25d366",
                  }}
                >
                  <WhatsAppOutlined />
                  Join Whatsapp Group
                </Button>
              </a>
            )}

            <Button
              type="link"
              onClick={() => setShowEntrollPopupForPaidCources(false)}
              style={{
                padding: 0,
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <Spin spinning={isLoading} tip="Loading..." size="large">
        <div className="course-continer">
          {userProfile?.role === "student" && nextInstallmentData && (
            <NextInstallmentBanner installmentData={nextInstallmentData} />
          )}
          <div className="course-header">
            <div className="about-course">
              <div className="title-flex">
                <h1 className="course-title">{course?.name}</h1>
              </div>

              {(() => {
                const assignedInstructor = instructors.find(
                  (inst) => inst.id === course?.instructorId
                );
                const instructorName = course?.instructorNames || course?.instructorName || assignedInstructor?.name;
                const instructorEmail = course?.instructorNames ? undefined : assignedInstructor?.email;
                if (instructorName) {
                  return (
                    <div
                      className="course-instructor-info"
                      style={{
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: "#1890ff",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Instructor:
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#333",
                        }}
                      >
                        {instructorName} {instructorEmail ? `(${instructorEmail})` : ""}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            <div className="">
              <div className="course-header-right">
                {course?.whatsappGroupLink && (
                  <div className="whatsapp-group">
                    {/* <h3>Join Our Whatsapp Group</h3> */}
                    <a
                      href={course?.whatsappGroupLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button
                        type="primary"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#25d366",
                        }}
                      >
                        <WhatsAppOutlined />
                        Join Whatsapp Group
                      </Button>
                    </a>
                  </div>
                )}
                {userProfile?.role === "student" && isCourseInUserCourses && (
                  <Button
                    type="default"
                    onClick={handleOpenFeedbackModal}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      borderColor: "#faad14",
                      color: "#d48806",
                      fontWeight: 600,
                      borderRadius: "6px",
                      marginLeft: "8px",
                    }}
                  >
                    <StarOutlined style={{ color: "#faad14", marginRight: 4 }} />
                    Give Feedback
                  </Button>
                )}
                {userProfile?.role === "student" && !isCourseInUserCourses && (
                  <Link to={`/dashboard/cources/${courseId}/buy`}>
                    <Button
                      type="primary"
                      size="large"
                      shape="round"
                      icon={<ShoppingCartOutlined />}
                      style={{
                        // marginTop: "60px",
                        height: "32px",
                      }}
                    >
                      Buy This Course
                    </Button>
                  </Link>
                )}
                 {isSuperAdmin && (
                  <div className="admin-actions">
                    {isSuperAdmin && (
                      <Button
                        type="default"
                        className="btn"
                        onClick={() => setOpenCourseUsers(true)}
                      >
                        <span>{courseUsers?.length}</span>
                        {isLoadingUsers ? <LoadingOutlined /> : <UserOutlined />}
                        <span>Users</span>
                      </Button>
                    )}

                    {(userProfile?.role === "superadmin" || userProfile?.role === "admin") && (
                      <Button
                        type="default"
                        className="btn"
                        onClick={() => {
                          setSelectedCoordinatorId(course?.coordinatorId || undefined);
                          setShowAssignModal(true);
                        }}
                      >
                        <UserOutlined />
                        <span>Assign Coordinator</span>
                      </Button>
                    )}

                     {(userProfile?.role === "superadmin" || userProfile?.role === "admin" || isCoordinator) && (
                       <Link to={`/dashboard/cources/${courseId}/edit`}>
                         <Button type="primary" className="btn">
                           <EditOutlined />
                           <span>Edit Course</span>
                         </Button>
                       </Link>
                     )}
                   </div>
                 )}
               </div>

              {userProfile?.role === "student" && (
                <div className="progress">
                  <h2>
                    <p>Your Study Progress</p>
                    <span>{completedPercentage + "%"}</span>
                  </h2>
                  <div className="progress-bar">
                    <Progress
                      percent={completedPercentage}
                      status="active"
                      strokeColor={{
                        from: "#108ee9",
                        to: "#87d068",
                      }}
                    />
                  </div>
                  <p>
                    The future belongs to those who believe in the beauty of their
                    dreams. - Eleanor Roosevelt
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="course-description-section" style={{ marginTop: "24px", marginBottom: "32px", padding: "24px", background: "#ffffff", borderRadius: "12px", border: "1px solid #f0f0f0", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)" }}>
            <h3 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#262626", marginBottom: "16px", borderBottom: "2px solid #1890ff", width: "fit-content", paddingBottom: "4px" }}>About This Course</h3>
            <div
              className="course-description ql-editor expanded"
              style={{ padding: 0, fontSize: "0.95rem", color: "#595959", lineHeight: "1.6" }}
              dangerouslySetInnerHTML={{ __html: course?.description }}
            />
          </div>

          {/* <div className='strat-date-and-time'>
                        {course?.startDate &&
                            <div className='course-start'>
                                <h2>Course Start Date</h2>
                                <p>{course?.startDate}</p>
                            </div>
                        }

                        {course?.startTime &&
                            <div className='course-start'>
                                <h2>Start Time:</h2>
                                <p>{course?.startTime}</p>
                            </div>
                        }
                    </div>

                    {
                        course?.videoUrl &&
                        <div className='description-video'>
                            <Plyr
                                source={{
                                    type: "video",
                                    sources: [
                                        {
                                            src: course?.videoUrl,
                                            provider: "youtube"
                                        }
                                    ],
                                }}
                            />
                        </div>
                    } */}

          <Divider />

          {isLoading || !courseContents?.length ? (
            <div className="no-data">
              {!isLoading && (
                <div>
                  <img src={nodata} alt="No Data" />
                  <h1>Course Content Will be Available Soon</h1>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="body">
                <div className="course-left">
                  {isContentUnlocked ? (
                    <span>
                      {(() => {
                        const selectedContent = courseContents?.find(
                          (item) => item.id === selectedContentId
                        );
                        if (!selectedContent)
                          return <h1>No video available</h1>;

                        let videoURL = selectedContent?.videoUrl;
                        let zoomURL = selectedContent?.zoomUrl;

                        // Always return a container with a stable key
                        return (
                          <div key={`content-container-${selectedContent.id}`}>
                            {videoURL ? (
                              <Plyr
                                source={{
                                  type: "video",
                                  sources: [
                                    {
                                      src: videoURL,
                                      provider: "youtube",
                                    },
                                  ],
                                }}
                              />
                            ) : zoomURL ? (
                              <div className="zoom-recording-wrapper">
                                <iframe
                                  src={zoomURL}
                                  className="zoom-iframe"
                                  title="Zoom Recording"
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    width: "100%",
                                    height: "600px",
                                    minHeight: "600px",
                                    borderRadius: "8px",
                                    border: "1px solid #e0e0e0",
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="no-data-mini">
                                {!isLoading && (
                                  <div>
                                    <img src={video} alt="No Data" />
                                    <h1>Video Will be Available Soon</h1>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {userProfile?.role === "superadmin" && (
                        <div
                          className="course-footer"
                          style={{ marginTop: "24px" }}
                        ></div>
                      )}

                      {userProfile?.role === "admin" && (
                        <div
                          className="course-footer"
                          style={{ marginTop: "24px" }}
                        ></div>
                      )}

                      {userProfile?.role === "student" && (
                        <div
                          className="course-footer"
                          style={{ marginTop: "24px" }}
                        >
                          <Button
                            loading={isEnrollLoading}
                            type="primary"
                            size="large"
                            onClick={handleMarkAsLearned}
                            className={
                              courseContents?.find(
                                (item) => item.id === selectedContentId
                              )?.participation?.id
                                ? "completed"
                                : ""
                            }
                            disabled={
                              courseContents?.find(
                                (item) => item.id === selectedContentId
                              )?.participation?.id
                            }
                          >
                            {courseContents?.find(
                              (item) => item.id === selectedContentId
                            )?.participation?.id
                              ? "Completed"
                              : "Mark As Completed"}
                          </Button>
                        </div>
                      )}
                      <Tabs
                        defaultActiveKey="overview"
                        items={[
                          {
                            key: "overview",
                            label: "Overview",
                            className: "overview-tab",
                            icon: <BookOpen size={18} />,
                            children: (
                              <>
                                <h2 className="content-title">
                                  {(() => {
                                    const selectedContent =
                                      courseContents?.find(
                                        (item) => item.id === selectedContentId
                                      );
                                    if (!selectedContent) return "";

                                    return selectedContent.title;
                                  })()}
                                </h2>
                                <div className="course-meta">
                                  <h2 className="description">
                                    ⏱️ Duration: {course?.duration || "N/A"}
                                  </h2>
                                  <h2 className="description">
                                    ⭐ Ratings: 4.8
                                  </h2>
                                  <h2 className="description">
                                    📝 Certified Students: 1000+
                                    {course?.totalStudents}
                                  </h2>
                                </div>
                                <Divider />
                                <h2>About This Lesson</h2>
                                <div
                                  className="ql-editor"
                                  style={{ padding: 0 }}
                                  dangerouslySetInnerHTML={{
                                    __html: (() => {
                                      const selectedContent =
                                        courseContents?.find(
                                          (item) => item.id === selectedContentId
                                        );

                                      if (!selectedContent) return "";

                                      return selectedContent.description;
                                    })()
                                  }}
                                />
                              </>
                            ),
                          },

                          {
                            key: "learning-tools",
                            label: "Course Materials",
                            icon: <FolderOpen size={18} />,
                            children: (
                              <div className="course-materials-wrapper">
                                {(() => {
                                  const selectedContent =
                                    courseContents?.find(
                                      (item) => item.id === selectedContentId
                                    );
                                  if (!selectedContent)
                                    return <h1>No Drive Folder available</h1>;

                                  const driveData = formatDriveUrl(selectedContent?.driveUrl);

                                  return (
                                    <>
                                      <div className="materials-header-flex">
                                        <div>
                                          <h2>Course Materials</h2>
                                          <p className="materials-subtitle">
                                            Study guides, worksheets, and resources for this lesson
                                          </p>
                                        </div>
                                        {driveData && (
                                          <Button
                                            type="primary"
                                            icon={<ExternalLink size={16} />}
                                            href={driveData.directUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="open-drive-btn"
                                          >
                                            Open in Google Drive
                                          </Button>
                                        )}
                                      </div>

                                      {driveData ? (
                                        <>
                                          <Alert
                                            message="Google Drive Access & 403 Error Tip"
                                            description={
                                              <div style={{ marginTop: 4, lineHeight: "1.6", color: "#374151" }}>
                                                <span>
                                                  If you see Google's <em>"403. That’s an error. You do not have access to this page"</em> or a blank box inside the preview below, your browser's third-party cookie blocking may be preventing Google from verifying your login.
                                                </span>
                                                <div style={{ marginTop: 8 }}>
                                                  <a
                                                    href={driveData.directUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ fontWeight: 600, color: "#1d4ed8", textDecoration: "underline" }}
                                                  >
                                                    Click here to Open directly in Google Drive ↗
                                                  </a>
                                                </div>
                                              </div>
                                            }
                                            type="info"
                                            showIcon
                                            closable
                                            style={{
                                              marginBottom: 20,
                                              borderRadius: 10,
                                              border: "1px solid #bfdbfe",
                                              backgroundColor: "#eff6ff",
                                              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
                                            }}
                                          />

                                          <div className="drive-floder">
                                            <iframe
                                              className="drive-iframe"
                                              title="Google Drive Folder"
                                              src={driveData.embedUrl}
                                              allow="autoplay; encrypted-media"
                                              allowFullScreen
                                            ></iframe>
                                          </div>
                                        </>
                                      ) : (
                                        <div className="no-data-mini">
                                          {!isLoading && (
                                            <div>
                                              <img src={files} alt="No Data" />
                                              <h1>
                                                Files Will be Available Soon
                                              </h1>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            ),
                          },
                          {
                            key: "notes",
                            label: "Notes & Material Downloads",
                            icon: <FileText size={18} />,
                            children: (
                              <Spin spinning={isNotesLoading}>
                                {courseNotes.length === 0 ? (
                                  <div className="no-data-mini" style={{ padding: "24px 0" }}>
                                    <h3>No compiled notes or study booklets published for this course yet.</h3>
                                  </div>
                                ) : (
                                  <List
                                    itemLayout="vertical"
                                    dataSource={courseNotes}
                                    renderItem={(item) => (
                                      <div className="note-card-glass" style={{
                                        background: "rgba(255, 255, 255, 0.02)",
                                        border: "1px solid rgba(255, 255, 255, 0.08)",
                                        borderRadius: "12px",
                                        padding: "20px",
                                        marginBottom: "16px"
                                      }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", alignItems: "center" }}>
                                          <span style={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.45)" }}>
                                            Published: {new Date(item.createdAt).toLocaleDateString()}
                                          </span>
                                          {item.isFree ? (
                                            <Tag color="green">Free Preview Note</Tag>
                                          ) : (
                                            <Tag color="blue">Premium Course Note</Tag>
                                          )}
                                        </div>
                                        <div 
                                          className="note-html-content"
                                          style={{ color: "#e3e3e3", lineHeight: "1.7", fontSize: "0.95rem" }}
                                          dangerouslySetInnerHTML={{ __html: item.note }} 
                                        />
                                        <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end" }}>
                                          <Button 
                                            type="default" 
                                            size="small" 
                                            onClick={() => {
                                              const printWindow = window.open("", "_blank");
                                              printWindow.document.write(`
                                                <html>
                                                  <head>
                                                    <title>Course Booklet Note</title>
                                                    <style>
                                                      body { font-family: sans-serif; padding: 40px; color: #333; line-height: 1.6; }
                                                      h1 { border-bottom: 2px solid #ccc; padding-bottom: 10px; }
                                                      .meta { color: #666; margin-bottom: 20px; font-size: 0.9em; }
                                                    </style>
                                                  </head>
                                                  <body>
                                                    <h1>${course?.name || "Course Notes"}</h1>
                                                    <div class="meta">Published Date: ${new Date(item.createdAt).toLocaleDateString()}</div>
                                                    <div>${item.note}</div>
                                                    <script>window.onload = function() { window.print(); }</script>
                                                  </body>
                                                </html>
                                              `);
                                              printWindow.document.close();
                                            }}
                                          >
                                            Download / Print Booklet
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  />
                                )}
                              </Spin>
                            ),
                          },
                          {
                            key: "reviews",
                            label: "Reviews",
                            icon: <Star size={18} />,
                            children: (
                              <>
                                <div>Reviews will be available soon.</div>
                              </>
                            ),
                          },
                        ]}
                      />
                    </span>
                  ) : (
                    <div className="buy-course-error">
                      <h1>Pay the next installment to access this content</h1>
                      <img src={buyImage} alt="Buy Course" />
                      <Link to={`/dashboard/cources/${courseId}/buy`}>
                        <Button
                          type="primary"
                          size="large"
                          shape="round"
                          icon={<ShoppingCartOutlined />}
                        >
                          Buy This Course
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>

                <div className="course-right">
                  <div className="course-content">
                    <h2>Course Contents</h2>
                    <div className="playlist-container">
                      <List
                        size="small"
                        bordered
                        dataSource={data}
                        renderItem={(item) => (
                          <List.Item
                            className={`${
                              selectedContentId === item.id ? "active" : ""
                            } ${
                              isContentLockedForUser(item.id) ? "locked" : ""
                            }`}
                            onClick={() =>
                              handleContentSelect(
                                item.id,
                                isContentLockedForUser(item.id)
                              )
                            }
                          >
                            <div className="btn" type="link">
                              {selectedContentId === item.id ? (
                                <PauseRoundedIcon
                                  style={{
                                    fontSize: "2rem",
                                    color: "#673bf9",
                                  }}
                                />
                              ) : (
                                <PlayArrowRoundedIcon
                                  style={{
                                    fontSize: "2rem",
                                    color: "#673bf9",
                                  }}
                                />
                              )}
                            </div>
                            <div className="course-content-item">
                              <p>{item.title}</p>
                              <span>{item.duration}</span>
                            </div>
                            <div className="status">
                              {item?.status === "completed" ? (
                                <div className="icon">
                                  <CheckRoundedIcon
                                    style={{
                                      fontSize: "1.2rem",
                                      color: "#fff",
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="icon not">
                                  <CheckRoundedIcon
                                    style={{
                                      fontSize: "1.2rem",
                                      color: "#fff",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </List.Item>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Divider />

              <div className="course-footer">
                <CourseAssessments courseId={courseId} course={course} />
              </div>
              {/* 
                            {userProfile?.role === 'superadmin' &&
                                <div className='course-footer'>
                                    <WishListUsers courseId={courseId} />
                                </div>
                            } */}
            </>
          )}
        </div>
      </Spin>

      <Modal
        title=""
        footer={null}
        centered
        open={showLockedContentPrompt}
        onCancel={() => setShowLockedContentPrompt(false)}
      >
        <div className="buy-course-error">
          <h1>
            {course?.price === 0 || course?.price === "0"
              ? "Enroll in this course to access this content"
              : userCources?.find((uc) => uc.courseId === courseId)
              ? "Pay the next installment to access this content"
              : "Enroll in this course to access this content"}
          </h1>
          <img src={buyImage} alt="Buy Course" />
          <Link to={`/dashboard/cources/${courseId}/buy`}>
            <Button
              type="primary"
              size="large"
              shape="round"
              icon={<ShoppingCartOutlined />}
            >
              {course?.price === 0 || course?.price === "0"
                ? "Enroll Now"
                : "Buy This Course"}
            </Button>
          </Link>
        </div>
      </Modal>

      <CourseUsers
        openCourseUsers={openCourseUsers}
        setOpenCourseUsers={setOpenCourseUsers}
        getCourseUsers={getCourseUsers}
        courseId={courseId}
        courseUsers={courseUsers}
        course={course}
      />

      {/* STUDENT FEEDBACK MODAL */}
      <Modal
        title={<span style={{ fontSize: 18, fontWeight: 700, color: "#03245e" }}>🎓 Course & Lecturer Feedback</span>}
        open={feedbackModalOpen}
        onCancel={() => setFeedbackModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setFeedbackModalOpen(false)}>Cancel</Button>,
          <Button
            key="submit"
            type="primary"
            loading={feedbackSubmitting}
            onClick={handleSubmitFeedback}
            style={{ background: "#03245e", borderColor: "#03245e" }}
          >
            Submit Feedback
          </Button>
        ]}
      >
        <div style={{ padding: "8px 0" }}>
          <p style={{ color: "#595959", marginBottom: 20 }}>
            Help us improve! Your rating and comments provide valuable feedback to your lecturer and future students.
          </p>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: "#262626" }}>1. Course Content & Curriculum Quality</div>
            <Rate allowHalf value={ratingCourse} onChange={setRatingCourse} style={{ color: "#faad14" }} />
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: "#262626" }}>2. Lecturer Teaching & Guidance</div>
            <Rate allowHalf value={ratingLecturer} onChange={setRatingLecturer} style={{ color: "#10b981" }} />
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: "#262626" }}>3. Content Clarity & Pace</div>
            <Rate allowHalf value={contentClarity} onChange={setContentClarity} style={{ color: "#1890ff" }} />
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#262626" }}>Your Comments or Suggestions:</div>
            <Input.TextArea
              rows={4}
              placeholder="Share your experience with the course, teaching style, materials..."
              value={feedbackComments}
              onChange={(e) => setFeedbackComments(e.target.value)}
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <Checkbox
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
            >
              Submit anonymously (your name will not be shown to lecturers)
            </Checkbox>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Course;
